import { TabContext, TabPanel } from "@mui/lab";
import ControlFilter from "components/shared/ControlFilter";
import type { IDateRangeShortcut } from "components/shared/DateRangeSelectors";
import DateRangeSelectors from "components/shared/DateRangeSelectors";
import type { LastElementTableAPIData } from "components/shared/LastElementTable/type";
import SmallSearchBar from "components/shared/SmallSearchBar";
import TitleConsolidatedSales from "components/shared/TitleConsolidatedSales";
import type { TOverriddenChannelsData } from "components/TitleFormContainer/types";
import { PortalType } from "contexts/portal.context";
import {
    formatLocaleDateString,
    getCleanStartorEndDay,
    getCurrentPeriod,
    groupObjByKey,
    sortByProperty,
} from "helpers/utils";
import useAPIPagination from "hooks/useAPIPagination";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useDebouncedCallback } from "hooks/useDebounce";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ChannelSales } from "store/analyticsStore";
import { fetchAnalyticsChannelsSales } from "store/analyticsStore";
import type { TPeriod } from "store/periodStore";
import { fetchPeriods } from "store/periodStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { EPortalType, type TFetchStatus } from "store/types";
import type { IDatesRange } from "types";

import styles from "./style.module.scss";
import TitleSalesTable from "./TitleSalesTable";

enum ETablesTab {
    GENERAL = "1",
    CONSOLIDATED = "2",
}

interface ITitleSalesContainer {
    titleId: number;
    titlePrice: number;
    titleChannelsData: TOverriddenChannelsData[];
}

const TitleSalesContainer = ({ titleId, titlePrice, titleChannelsData }: ITitleSalesContainer) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const portalType = useContext(PortalType);
    const { activePeriod, periods, periodsDateRange } = useAppSelector((state) => state.period);
    const { titleSales, titleSalesCount, titleFirstSaleDate } = useAppSelector((state) => state.titles);
    const { analyticsChannelsSales } = useAppSelector((state) => state.analytics);
    const { channels } = useAppSelector((state) => state.channels);

    const [tablesTab, setTablesTab] = useState(ETablesTab.GENERAL);
    const [currentPeriod, setCurrentPeriod] = useState<TPeriod | undefined>(getCurrentPeriod(periods));
    const [consolidatedSalesStatus, setConsolidatedSalesStatus] = useState<TFetchStatus>("idle");

    const { getCompanySettings } = useCompanySettings();
    const showTitleConsolidatedSales = getCompanySettings("show_title_consolidated_sales");

    const consolidatedChannelsData = analyticsChannelsSales["channels-metrics"].data;

    /**
     * Check if sales loaded come from different title (to trigger new fetch).
     */
    const differentTitle = useMemo(() => {
        if (!titleId || !titleSales) return;
        if (titleSales.length > 0) {
            return titleSales.every((sale) => sale.titleId !== titleId);
        } else {
            return true;
        }
    }, [titleId, titleSales]);

    const {
        query,
        handleQuery,
        page,
        limitPagination,
        sortBy,
        sortOrder,
        handleChangePage,
        handleChangeLimit,
        handleSort,
        handleRequest,
        requestStatus,
    } = useAPIPagination({
        type: "titleSales",
        itemId: titleId,
        sortBy: "date",
        sortOrder: "desc",
        condition: differentTitle,
    });

    // The hook skips the fetch when cached sales already match this title, and stays "idle".
    // Idle means "not started" for the table, so report the cache as loaded.
    const salesStatus = requestStatus === "idle" && differentTitle === false ? "succeeded" : requestStatus;

    const APIData = {
        query,
        handleQuery,
        page,
        count: titleSalesCount,
        limitPagination,
        sortBy,
        sortOrder,
        handleChangePage,
        handleChangeLimit,
        handleSort,
        handleRequest,
    } as unknown as LastElementTableAPIData;

    const tablesOptions = [
        { value: ETablesTab.GENERAL, label: t("TitleSalesTable.GeneralView") },
        { value: ETablesTab.CONSOLIDATED, label: t("TitleSalesTable.CondensedView") },
    ];

    /**
     * Groups data from analyticsChannelsSales into categories.
     * @returns Object
     */
    const hasNoConsolidatedSales = consolidatedSalesStatus === "succeeded" && consolidatedChannelsData.length === 0;

    const channelsCategoriesData: Record<number, ChannelSales[]> | undefined = useMemo(() => {
        if (hasNoConsolidatedSales) return undefined;

        const allChannels = channels.map((channel) => ({
            name: channel.name,
            id: channel.id,
            kind: channel.kind,
            data: { total_channel_id_quantities: 0, total_channel_id_turnover: 0 },
        }));
        const formattedChannels = groupObjByKey("kind", allChannels) as Record<number, ChannelSales[]>;
        if (consolidatedChannelsData.length) {
            consolidatedChannelsData.forEach((channel) => {
                const channelIndex = formattedChannels[channel.channelKind].findIndex(
                    (item) => item.id === channel.channelId,
                );
                formattedChannels[channel.channelKind][channelIndex] = {
                    name: channel.channelName,
                    id: channel.channelId,
                    kind: channel.channelKind,
                    data: {
                        total_channel_id_quantities: channel.total_channel_id_quantities,
                        total_channel_id_turnover: channel.total_channel_id_turnover,
                    },
                };
            });
        }

        return formattedChannels;
    }, [analyticsChannelsSales, channels, consolidatedChannelsData, hasNoConsolidatedSales]);

    /**
     * Earliest and latest sale date from titleSales.
     * Needed for dates selector to know max date allowed.
     * These dates may be further away than periods.
     * Fallback is always periods.
     */
    const furthestSaleDates: IDatesRange | undefined = useMemo(() => {
        if (!titleSales.length) return;
        const sortedArray = sortByProperty(titleSales, "date");
        if (sortedArray) {
            // Rebuild local Dates from the UTC calendar day so the picker (local TZ) shows
            // the same day the backend stored — Toronto would otherwise shift to J-1.
            return {
                startDate: getCleanStartorEndDay(titleFirstSaleDate ?? periodsDateRange.startDate),
                endDate: getCleanStartorEndDay(sortedArray[0].date, { time: "end" }),
            };
        }
    }, [titleSales]);

    const consolidatedDateRange = furthestSaleDates ?? periodsDateRange;
    const hasConsolidatedDates = Boolean(consolidatedDateRange?.startDate && consolidatedDateRange?.endDate);

    /**
     * Fetches periods data in case activePeriod doesn't exist.
     */
    useEffect(() => {
        if ((!activePeriod || !currentPeriod) && portalType !== EPortalType.BENEFICIARY) {
            dispatch(fetchPeriods()).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    setCurrentPeriod(getCurrentPeriod(resp.payload));
                }
            });
        }
    }, [activePeriod, currentPeriod]);

    // On first arriving on tab, fetches data.
    useEffect(() => {
        if (tablesTab !== ETablesTab.CONSOLIDATED) return;
        if (!hasConsolidatedDates) {
            setConsolidatedSalesStatus("pending");
            return;
        }

        let cancelled = false;
        setConsolidatedSalesStatus("pending");
        dispatch(fetchAnalyticsChannelsSales({ dates: consolidatedDateRange, titleId, type: "channels-metrics" })).then(
            (resp) => {
                if (cancelled) return;
                setConsolidatedSalesStatus(resp.meta.requestStatus === "fulfilled" ? "succeeded" : "failed");
            },
        );

        return () => {
            cancelled = true;
        };
    }, [consolidatedDateRange, dispatch, hasConsolidatedDates, tablesTab, titleId]);

    /**
     * Fetches categories data after change of dates.
     */
    const fetchNewData = useDebouncedCallback((updatedDates) => {
        dispatch(fetchAnalyticsChannelsSales({ dates: updatedDates, titleId, type: "channels-metrics" })).then(
            (resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    setConsolidatedSalesStatus("succeeded");
                } else {
                    setConsolidatedSalesStatus("failed");
                }
            },
        );
    }, 2000);

    /**
     * Updates filterDates and triggers new fetch with debounce.
     * @param updatedDates New dates
     */
    const handleDatesChange = (updatedDates: IDatesRange | undefined) => {
        setConsolidatedSalesStatus("pending");
        fetchNewData(updatedDates);
    };

    /**
     * Shortcuts for dates selector.
     */
    const rangeShortcuts = useCallback((): IDateRangeShortcut[] => {
        if (!periods) return [];
        const activePeriods = periods.filter((item) => item.isActive);
        const lastClosedPeriods = periods.filter((item) => item.isActive === 0).slice(0, 3);

        const range: IDateRangeShortcut[] = [];
        [...activePeriods, ...lastClosedPeriods].forEach((period) => {
            range.push({
                rangeKey: period.id,
                label: `${formatLocaleDateString({
                    date: period.periodStart,
                    options: { numeric: true, timeZone: "UTC" },
                })} - ${formatLocaleDateString({
                    date: period.periodEnd,
                    options: { numeric: true, timeZone: "UTC" },
                })}`,
                dates: { startDate: period.periodStart, endDate: period.periodEnd },
                open: !!period.isActive,
            });
        });
        return range;
    }, [periods]);

    const getMaxEndDate = (date: Date | undefined) => {
        if (!date) return;

        // If furthestSaleDate.endDate is higher than today:
        if (new Date(date) > new Date()) {
            // Need to clone so that original date isn't updated
            // Not having this caused errors.
            const newDate = new Date(date.getTime());
            newDate.setFullYear(newDate.getFullYear() + 1);
            return newDate;
        } else {
            // If not, maxDate is today.
            return new Date();
        }
    };

    return (
        <div className={styles["title-sales-container"]}>
            <div className={styles["sales-filters"]}>
                {tablesTab === ETablesTab.CONSOLIDATED ? (
                    <DateRangeSelectors
                        dateRange={periodsDateRange}
                        defaultValues={furthestSaleDates}
                        handleChanges={handleDatesChange}
                        label={t("TitleSalesTable.ConsolidatedTable.DataBetween")}
                        rangeShortcuts={rangeShortcuts()}
                        maxEndDate={getMaxEndDate(furthestSaleDates?.endDate)}
                    />
                ) : (
                    <SmallSearchBar
                        filter={query}
                        onSearch={handleQuery}
                        placeholder={t("TitleSalesTable.SearchChannel")}
                    />
                )}
                {showTitleConsolidatedSales && (
                    <ControlFilter
                        filterOptions={tablesOptions}
                        controlValue={tablesTab}
                        onValueChange={(value) => setTablesTab(value)}
                        filterLabel={t("TitleSalesTable.View")}
                    />
                )}
            </div>
            <TabContext value={tablesTab}>
                <TabPanel value={ETablesTab.GENERAL}>
                    <TitleSalesTable
                        data={titleSales}
                        dataStatus={salesStatus}
                        APIData={APIData}
                        titlePrice={titlePrice}
                        titleChannelsData={titleChannelsData}
                        currentPeriod={currentPeriod}
                        titleId={titleId}
                    />
                </TabPanel>
                <TabPanel value={ETablesTab.CONSOLIDATED}>
                    <TitleConsolidatedSales
                        categoriesData={channelsCategoriesData}
                        dataStatus={consolidatedSalesStatus}
                    />
                </TabPanel>
            </TabContext>
        </div>
    );
};

export default TitleSalesContainer;
